import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, AlertCircle, Loader2, Shield, Eye, EyeOff, Smartphone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

import { API_URL } from '../../utils/apiConfig';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, submitting, error, requires_2fa
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPasswordStatus, setForgotPasswordStatus] = useState('idle'); // idle, submitting, success

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordStatus('submitting');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotEmail }),
      });
      
      // Always show success (to prevent email enumeration)
      setForgotPasswordStatus('success');
    } catch (err) {
      // Still show success to prevent enumeration
      setForgotPasswordStatus('success');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password,
          totp_code: requires2FA ? totpCode : undefined
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setStatus('error');
        setError(data?.detail || 'Login failed');
        return;
      }

      // Check if 2FA is required
      if (data.requires_2fa) {
        setRequires2FA(true);
        setStatus('idle');
        return;
      }

      // Store token and admin flag
      localStorage.setItem('pecash_token', data.token);
      localStorage.setItem('pecash_admin', 'true');
      localStorage.setItem('pecash_admin_email', data.user.email);
      
      // Show password expiry warning if applicable
      if (data.password_warning) {
        toast.warning(`Your password expires in ${data.days_until_expiry} days`);
      }
      
      // Check if password expired - redirect to change password
      if (data.password_expired) {
        toast.error('Your password has expired. Please change it.');
        navigate('/admin/change-password');
        return;
      }
      
      // Navigate to admin dashboard
      navigate('/admin/dashboard');
      
    } catch (networkError) {
      setStatus('error');
      setError('Network error: Unable to connect to the server');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
      <motion.div
        initial="initial"
        animate="animate"
        variants={fadeInUp}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="font-bold text-2xl text-white">PeCashGlobal</span>
            <span className="text-purple-400 text-sm ml-2 font-medium">Admin</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-3xl shadow-2xl p-8" data-testid="admin-login-card">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-purple-400" />
            <h2 className="text-2xl font-bold text-white text-center">
              Admin Sign In
            </h2>
          </div>
          <p className="text-slate-400 text-center mb-8">
            Authorized administrators only
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="space-y-4">
              {/* Email Field */}
              <div>
                <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your admin email"
                    required
                    autoComplete="email"
                    data-testid="admin-email-input"
                    className="pl-10 h-12 rounded-xl bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    data-testid="admin-password-input"
                    className="pl-10 pr-10 h-12 rounded-xl bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* 2FA Code Field - Only shown when required */}
              {requires2FA && (
                <div className="p-4 bg-purple-900/30 border border-purple-700 rounded-xl">
                  <Label htmlFor="totp" className="text-purple-300 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Two-Factor Authentication Code
                  </Label>
                  <Input
                    id="totp"
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    autoComplete="one-time-code"
                    data-testid="admin-2fa-input"
                    className="mt-2 h-12 text-center text-2xl font-mono tracking-widest rounded-xl bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                    maxLength={6}
                  />
                  <p className="text-xs text-purple-400 mt-2">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
              )}
              
              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                disabled={status === 'submitting' || (requires2FA && totpCode.length !== 6)}
                data-testid="admin-login-btn"
                className="w-full h-12 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold mt-4"
              >
                {status === 'submitting' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {requires2FA ? 'Verify & Sign In' : 'Sign In'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>

          <div className="mt-6 p-4 bg-amber-900/20 border border-amber-800/50 rounded-xl">
            <p className="text-center text-sm text-amber-300/80">
              All login attempts are logged and audited for security.
            </p>
          </div>
        </div>

        <p className="text-center text-slate-500 mt-6 text-sm">
          Not an admin? <Link to="/login" className="text-purple-400 hover:underline">Go to regular login</Link>
        </p>
      </motion.div>
      
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 rounded-2xl p-6 w-full max-w-md border border-purple-500/20"
          >
            <h3 className="text-xl font-bold text-white mb-2">Reset Password</h3>
            <p className="text-slate-400 text-sm mb-6">
              Enter your admin email address and we'll send you a link to reset your password.
            </p>
            
            {forgotPasswordStatus === 'success' ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-emerald-400 font-medium mb-2">Check your email!</p>
                <p className="text-slate-400 text-sm">
                  If an admin account exists with this email, you will receive a password reset link shortly.
                </p>
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordStatus('idle');
                    setForgotEmail('');
                  }}
                  className="mt-4 bg-slate-700 hover:bg-slate-600"
                >
                  Close
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgotEmail" className="text-slate-300">Admin Email</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="forgotEmail"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your admin email"
                      required
                      data-testid="forgot-email-input"
                      className="pl-10 h-12 rounded-xl bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordStatus('idle');
                      setForgotEmail('');
                    }}
                    className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={forgotPasswordStatus === 'submitting' || !forgotEmail}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    data-testid="send-reset-link-btn"
                  >
                    {forgotPasswordStatus === 'submitting' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
